// Mock data for Pallaw Pathak's Portfolio

export const personalInfo = {
  name: "Pallaw Pathak",
  title: "Senior Mobile Engineer | Mobile Architecture | Platform Engineering | Kotlin Multiplatform",
  subtitle: "10+ Years Experience | Mobile Architecture, Platform Engineering & Kotlin Multiplatform Specialist",
  bio: "Senior Mobile Engineer with 10+ years of experience building scalable mobile systems across Android and cross-platform ecosystems. Specialized in mobile architecture, Kotlin Multiplatform, and platform engineering, with a focus on modular design, developer productivity, and system scalability. Experienced in working on high-scale applications (up to 100M+ downloads) and improving performance and stability across fintech, edtech, logistics, and consumer platforms, with recent work in mobile observability (OpenTelemetry, Embrace, SigNoz) and AI-assisted development workflows.",
  profileImage: "https://customer-assets.emergentagent.com/job_3c2bde26-baa1-4dd6-a6c3-4b7f71bc0705/artifacts/zv3h7a9v_profile-pic%20%283%29.png",
  email: "pallaw.pathak@gmail.com",
  phone: "+917696487712",
  whatsapp: "+917696487712",
  linkedin: "https://www.linkedin.com/in/pallawpathak/",
  linkedinMessaging: "https://www.linkedin.com/messaging/compose/?recipient=pallawpathak",
  github: "https://github.com/pallaw",
  medium: "https://medium.com/@pallawpathak",
  calendly: "https://calendly.com/pallaw-pathak/30min?hide_event_type_details=1&hide_gdpr_banner=1",
  location: "India"
};

export const about = {
  description: "I'm a Senior Mobile Engineer with 10+ years of experience building scalable mobile systems across Android and cross-platform ecosystems. I specialize in mobile architecture, Kotlin Multiplatform, and platform engineering — with a focus on modular design, developer productivity, and system scalability. I've shipped apps with up to 100M+ downloads across fintech, edtech, and logistics platforms.",
  aiStatement: "I build AI-first. I use Claude Code, Cursor, and AI-assisted workflows daily to move faster, write better code, and solve harder problems. AI isn't a tool I occasionally reach for — it's core to how I engineer.",
  highlights: [
    {
      icon: "trophy",
      title: "10+ Years",
      description: "Professional Experience"
    },
    {
      icon: "users",
      title: "100M+ Downloads",
      description: "Across All Projects"
    },
    {
      icon: "award",
      title: "Hackathon Winner",
      description: "PayPay India 2025"
    },
    {
      icon: "code",
      title: "85% Code Sharing",
      description: "With Kotlin Multiplatform (KMP)"
    }
  ],
  interests: [
    { icon: "music", label: "Music & Guitar" },
    { icon: "gamepad-2", label: "Chess" },
    { icon: "pen-tool", label: "Writing" },
    { icon: "table-tennis", label: "Table Tennis" }
  ]
};

export const experience = [
  {
    id: 1,
    company: "Moniepoint",
    position: "Senior Mobile Engineer (Platform Team)",
    duration: "Feb 2026 - Present",
    location: "Remote",
    responsibilities: [
      "Implemented mobile observability using OpenTelemetry (Embrace, SigNoz), improving visibility into app performance, network calls, and production issues",
      "Improved platform-level instrumentation and debugging workflows, enabling faster issue detection and resolution"
    ],
    technologies: ["Kotlin", "OpenTelemetry", "Embrace", "SigNoz", "Platform Engineering"]
  },
  {
    id: 2,
    company: "Independent Mobile Architecture Consultant",
    position: "Mobile Architecture Consultant",
    duration: "Jul 2025 - Jan 2026",
    location: "Remote",
    responsibilities: [
      "Built TipABish, a Kotlin Multiplatform-based live tipping platform, owning end-to-end development from product design (UI/UX) to scalable architecture",
      "Designed and implemented shared business logic using Kotlin Multiplatform, enabling cross-platform scalability"
    ],
    technologies: ["Kotlin Multiplatform (KMP)", "Compose Multiplatform (CMP)", "Clean Architecture"]
  },
  {
    id: 3,
    company: "Wiom",
    position: "Lead Mobile Developer",
    duration: "May 2025 - Jul 2025",
    location: "Remote",
    responsibilities: [
      "Contributed to mobile architecture discussions for a Flutter-based telecom app, proposing scalable patterns inspired by Clean Architecture and MVI"
    ],
    technologies: ["Flutter", "Clean Architecture", "MVI"]
  },
  {
    id: 4,
    company: "PayPay India",
    position: "Senior Software Engineer",
    duration: "Jan 2025 - May 2025",
    location: "Gurugram",
    responsibilities: [
      "Optimized CI/CD pipelines for mobile SDK development using GitHub Actions, streamlining build and release workflows",
      "Built SmaToshi, a goal-based micro-investment prototype, winning the internal PayPay Hackathon"
    ],
    technologies: ["Kotlin", "GitHub Actions", "CI/CD", "Clean Architecture", "Multi-Module Architecture"]
  },
  {
    id: 5,
    company: "PhysicsWallah",
    position: "Senior Software Engineer",
    duration: "Aug 2023 - Dec 2024",
    location: "Noida",
    responsibilities: [
      "Architected and built AcadFly using Kotlin Multiplatform with ~85% shared code across Android and iOS",
      "Led Android architecture for CuriousJunior (1M+ downloads), modernizing the codebase using multi-module architecture",
      "Contributed to mobile platforms within the PhysicsWallah ecosystem (100M+ downloads), improving performance and stability at scale",
      "Reduced build times by ~30% and improved stability to 96%+ crash-free users"
    ],
    technologies: ["Kotlin Multiplatform (KMP)", "Compose Multiplatform (CMP)", "MVVM", "MVI", "Multi-module Architecture", "Coroutines"]
  },
  {
    id: 6,
    company: "Shipsy",
    position: "Senior Software Engineer",
    duration: "Aug 2020 - Jun 2023",
    location: "Gurugram",
    responsibilities: [
      "Led refactoring of a legacy logistics app using Clean Architecture and MVVM, improving scalability and maintainability",
      "Reduced crash rates by ~20% and improved performance by ~30%",
      "Implemented barcode scanning (HHT devices), reducing operational processing time by ~40%",
      "Established internal Android engineering standards and best practices"
    ],
    technologies: ["MVVM", "Clean Architecture", "Kotlin", "RxJava", "Dagger-Hilt"]
  },
  {
    id: 7,
    company: "Small World (Crafts Inc.)",
    position: "Senior Software Engineer",
    duration: "Aug 2019 - Mar 2020",
    location: "Nagoya, Japan (Remote)",
    responsibilities: [
      "Built Android app for students with job search, visa processing, and accommodation in Japan",
      "Reached 5K+ users within the first quarter"
    ],
    technologies: ["Kotlin", "MVVM", "Retrofit", "Firebase"]
  },
  {
    id: 8,
    company: "XBios",
    position: "Software Engineer",
    duration: "Sep 2017 - Jul 2019",
    location: "Gurugram",
    responsibilities: [
      "Contributed to feature development and SDK maintenance for large-scale e-commerce apps",
      "Promoted from SDE1 to SDE2 based on performance"
    ],
    technologies: ["Java", "Android", "SDK Development"]
  },
  {
    id: 9,
    company: "Recon AppMagic",
    position: "Software Engineer",
    duration: "May 2015 - Aug 2017",
    location: "Gurugram",
    responsibilities: [
      "Independently led Android projects from concept to deployment",
      "Collaborated with product teams for scalable delivery"
    ],
    technologies: ["Java", "Android", "MVP"]
  }
];

export const skills = {
  languages: [
    { name: "Kotlin", years: 7, rating: 4, maxRating: 5 },
    { name: "Java", years: 10, rating: 4, maxRating: 5 },
    { name: "Kotlin Multiplatform", years: 2, rating: 3, maxRating: 5 },
    { name: "Compose Multiplatform", years: 2, rating: 3, maxRating: 5 }
  ],
  categories: [
    {
      id: "ai",
      title: "AI-Assisted Engineering",
      icon: "bot",
      featured: true,
      items: ["Claude Code", "Cursor", "AI-assisted Workflows", "Vibe Coding"]
    },
    {
      id: "architecture",
      title: "Mobile Architecture",
      icon: "layers",
      items: ["Clean Architecture", "MVVM", "MVI", "Modular Architecture", "Scalable Systems"]
    },
    {
      id: "crossplatform",
      title: "Cross-Platform & Android",
      icon: "smartphone",
      items: ["Kotlin Multiplatform (KMP)", "Compose Multiplatform (CMP)", "Jetpack Compose", "Kotlin", "Coroutines", "Flow"]
    },
    {
      id: "platform",
      title: "Platform Engineering",
      icon: "git-branch",
      items: ["CI/CD", "GitHub Actions", "Mobile DevOps", "Build Optimization"]
    },
    {
      id: "observability",
      title: "Observability",
      icon: "activity",
      items: ["OpenTelemetry", "Embrace", "SigNoz", "Firebase Crashlytics"]
    },
    {
      id: "tooling",
      title: "Data & Tooling",
      icon: "wrench",
      items: ["Ktor", "Retrofit", "REST APIs", "Koin", "Dagger-Hilt", "JUnit", "MockK", "Kotest", "Git"]
    }
  ]
};

export const projects = [
  {
    id: 1,
    name: "TipABish",
    description: "Kotlin Multiplatform-based live tipping platform built end-to-end as an independent consultant. Owned product design (UI/UX) and scalable architecture with ~85% shared business logic across Android and iOS.",
    image: "/tipabish-logo.png",
    technologies: ["Kotlin Multiplatform (KMP)", "Compose Multiplatform (CMP)", "Clean Architecture", "Ktor", "Koin"],
    metrics: {},
    links: {},
    company: "Independent"
  },
  {
    id: 3,
    name: "PayPay",
    description: "Japan's leading mobile payment platform. Contributed to SDK development and CI/CD pipeline optimization; built SmaToshi, a goal-based micro-investment prototype that won the internal PayPay Hackathon.",
    image: "https://play-lh.googleusercontent.com/AieC1ypSSh_2rctvrNtVggyFRP9cNtvnEIPkVmzZGFlhN8bNdHCl3GZbxK7O8vCe7A=w240-h480-rw",
    technologies: ["Kotlin", "GitHub Actions", "CI/CD", "Android SDK"],
    metrics: {
      downloads: "10M+",
      rating: "4.5"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=jp.ne.paypay.android.app&hl=en_IN"
    },
    company: "PayPay India"
  },
  {
    id: 4,
    name: "AcadFly",
    description: "Study Abroad Platform built from scratch with Kotlin Multiplatform. Achieved ~85% code sharing between Android and iOS, resulting in faster development and consistent UX.",
    image: "https://play-lh.googleusercontent.com/UTBbl7ZiI-gXLtij7LqzFVk1R6HhwI9dqT9IYUt7fyjoz3eFfxxjhrTFmQ6y-p2cgrk=w240-h480-rw",
    technologies: ["Kotlin Multiplatform (KMP)", "Compose Multiplatform (CMP)", "Ktor", "Koin", "MVVM"],
    metrics: {
      downloads: "10K+",
      rating: "4.0"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=xyz.penpencil.unigo&hl=en_IN",
      appStore: "https://apps.apple.com/in/app/acadfly-study-abroad/id6737373703"
    },
    company: "PhysicsWallah"
  },
  {
    id: 5,
    name: "PhysicsWallah",
    description: "Core contributor to India's leading EdTech platform (100M+ downloads). Improved performance, stability, and architecture at scale across the PhysicsWallah mobile ecosystem.",
    image: "https://play-lh.googleusercontent.com/x5_bJXO_-4zH95U6-GSmZPHWX7iFe1JPmfGe26kV2fVrZS5Ebi7L_VFIOopAQ3XBDg=w240-h480-rw",
    technologies: ["Kotlin", "Jetpack Compose", "Multi-module", "Coroutines", "Clean Architecture"],
    metrics: {
      downloads: "100M+",
      rating: "4.6"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=xyz.penpencil.physicswala&hl=en_IN",
      appStore: "https://apps.apple.com/in/app/physics-wallah/id1577604498"
    },
    company: "PhysicsWallah"
  },
  {
    id: 6,
    name: "HubOps by Shipsy",
    description: "Middle-mile logistics management app. Integrated HHT barcode scanning, reducing processing time by 40%. Improved crash-free users from 75% to 96%+.",
    image: "https://play-lh.googleusercontent.com/aWtMNzMSzvjr-tfORscKwHHPoJfkea-LbnxxKvyosudp7Bj0dIKSbBldpjMSFjo76w4=w240-h480-rw",
    technologies: ["Kotlin", "MVVM", "Barcode Scanning", "Dagger-Hilt", "Retrofit"],
    metrics: {
      downloads: "10K+",
      rating: "4.3"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=in.shipsy.riderapp.hubOps&hl=en_IN"
    },
    company: "Shipsy"
  },
  {
    id: 7,
    name: "FieldOps by Shipsy",
    description: "Field operations management solution for logistics teams. Built with scalable architecture and real-time tracking capabilities.",
    image: "https://play-lh.googleusercontent.com/8cbfbn7OYCyDl3jcJm34iuh0qIsGkpqZ92TRCS2tNpJJLaOimgRBmvzlPiPMMMJwQ9ef=w240-h480-rw",
    technologies: ["Kotlin", "MVVM", "Google Maps", "Firebase", "Room"],
    metrics: {
      downloads: "5K+",
      rating: "4.2"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=io.shipsy.fieldops&hl=en_IN"
    },
    company: "Shipsy"
  },
  {
    id: 8,
    name: "TMS Rider by Shipsy",
    description: "Transportation Management System for delivery partners. Focused on performance optimization and reliability for mission-critical operations.",
    image: "https://play-lh.googleusercontent.com/qjq2WeZkN57elgL1jbzTxc7BJgtj205hKyrzfIg8dEwtrQjCEEzsBOfqCrzfVFE-hg=w240-h480-rw",
    technologies: ["Kotlin", "Clean Architecture", "Coroutines", "Retrofit"],
    metrics: {
      downloads: "5K+",
      rating: "4.1"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=in.shipsy.riderapp.tms&hl=en_IN"
    },
    company: "Shipsy"
  },
  {
    id: 9,
    name: "OnDemand by Shipsy",
    description: "On-demand delivery management platform. Implemented real-time order tracking and optimized routing algorithms.",
    image: "https://play-lh.googleusercontent.com/iByeglWn9_Q6upqvHjBJ_IAj3fVAQ5amPmkeaAy5JLyXzE9OvyWa7xyPYhCit9vsiQ=w240-h480-rw",
    technologies: ["Kotlin", "MVVM", "Firebase", "Google Maps API"],
    metrics: {
      downloads: "3K+",
      rating: "4.0"
    },
    links: {
      playStore: "https://play.google.com/store/apps/details?id=in.shipsy.riderapp.ondemand.generic&hl=en_IN"
    },
    company: "Shipsy"
  }
];

export const hireMe = {
  title: "Let's Work Together",
  description: "I specialize in building scalable cross-platform mobile applications using Kotlin Multiplatform and Compose Multiplatform. With over a decade of experience delivering high-impact products, I bring deep technical expertise and proven leadership to drive your mobile strategy forward.",
  availability: "Available for consulting & leadership roles",
  cta: "Let's Connect",
  whyWorkWithMe: [
    { icon: "clock", stat: "10+ Years", label: "Mobile Engineering" },
    { icon: "smartphone", stat: "85% Code Share", label: "Kotlin Multiplatform Expert" },
    { icon: "bot", stat: "AI-First", label: "Claude Code & Cursor Daily" },
    { icon: "download", stat: "100M+ Downloads", label: "Apps Shipped at Scale" },
  ],
  services: [
    {
      icon: "code",
      title: "Cross-Platform Development",
      description: "Build iOS and Android apps with 85% shared code using Kotlin Multiplatform (KMP) and Compose Multiplatform (CMP)",
      tags: ["KMP", "CMP", "iOS", "Android"]
    },
    {
      icon: "layout",
      title: "Architecture & Design",
      description: "Design scalable, maintainable architectures using MVVM, MVI, and Clean Architecture patterns",
      tags: ["MVVM", "MVI", "Clean Architecture", "Multi-module"]
    },
    {
      icon: "zap",
      title: "Performance Optimization",
      description: "Optimize app performance, reduce crash rates, and improve user experience metrics",
      tags: ["Performance", "Optimization", "Crash Analysis"]
    },
    {
      icon: "users-round",
      title: "Team Leadership & Mentorship",
      description: "Lead development teams, mentor junior developers, and establish best practices",
      tags: ["Leadership", "Mentorship", "Code Review"]
    },
    {
      icon: "git-branch",
      title: "CI/CD & DevOps",
      description: "Set up automated pipelines, streamline release processes, and improve developer productivity",
      tags: ["GitHub Actions", "CI/CD", "Automation"]
    },
    {
      icon: "smartphone",
      title: "Legacy App Modernization",
      description: "Refactor and modernize legacy codebases with modern architecture and latest technologies",
      tags: ["Refactoring", "Migration", "Modernization"]
    }
  ]
};

export const currentWork = [
  {
    id: 'moniepoint-personal',
    name: 'Moniepoint Personal',
    company: 'Moniepoint',
    role: 'Full-time',
    roleLabel: 'Full-time · Platform Team',
    description: 'Personal banking app serving millions of Africans with seamless digital financial services. Contributing to mobile observability, platform instrumentation, and app stability as part of the Platform Engineering team.',
    image: '/moniepoint-personal.png',
    technologies: ['Kotlin', 'OpenTelemetry', 'Embrace', 'SigNoz', 'Platform Engineering'],
    playStore: 'https://play.google.com/store/apps/details?id=com.moniepoint.personal',
    appStore: 'https://apps.apple.com/us/app/moniepoint-personal-banking/id6452756675',
    status: 'active',
    metrics: { downloads: '1Cr+', rating: '4.6' },
    badge: 'Contributor',
  },
  {
    id: 'moniepoint-business',
    name: 'Moniepoint Business',
    company: 'Moniepoint',
    role: 'Full-time',
    roleLabel: 'Full-time · Platform Team',
    description: 'Business banking solution for merchants and enterprises across Africa. Working on platform-level reliability, observability tooling, and performance improvements that impact production stability.',
    image: '/moniepoint-business.png',
    technologies: ['Kotlin', 'OpenTelemetry', 'Platform Engineering', 'Mobile DevOps'],
    playStore: 'https://play.google.com/store/apps/details?id=com.moniepoint.business',
    appStore: 'https://apps.apple.com/us/app/moniepoint-business-banking/id1661134941',
    status: 'active',
    metrics: { downloads: '1Cr+', rating: '4.6' },
    badge: 'Contributor',
  },
  {
    id: 'tipabish',
    name: 'TipABish',
    company: 'Independent',
    role: 'Part-time',
    roleLabel: 'Part-time · Independent',
    description: 'Kotlin Multiplatform live tipping platform built end-to-end as an independent project. Owning product design (UI/UX) and scalable architecture with ~85% shared business logic across Android and iOS.',
    image: '/tipabish-logo.png',
    technologies: ['Kotlin Multiplatform (KMP)', 'Compose Multiplatform (CMP)', 'Clean Architecture', 'Ktor'],
    playStore: null,
    appStore: null,
    status: 'in-progress',
    metrics: {},
    badge: 'Solo Project',
  },
];

export const articles = [
  {
    id: 1,
    title: "Unified UI with Compose Multiplatform (CMP): Getting Started with Compose Multiplatform 'Hello World'",
    excerpt: "Ready to build one app that works on Android, iOS, and desktop? Compose Multiplatform lets you share UI code across platforms.",
    date: "Oct 11, 2024",
    readTime: "5 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*nqHTiq4bEZ4EHKJtbDn66g.jpeg",
    link: "https://medium.com/@pallawpathak/unified-ui-with-cmp-getting-started-with-compose-multiplatform-hello-world-8c5e62cdce0e"
  },
  {
    id: 2,
    title: "Version Catalog: Pivot Your Android Dependency Management to a Smoother Setup",
    excerpt: "Learn how Version Catalogs can streamline your Android project's dependency management for better maintainability.",
    date: "Aug 14, 2024",
    readTime: "4 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*XWcW4tZGgECWRdm2MxPvvQ.jpeg",
    link: "https://medium.com/@pallawpathak/version-catalog-pivot-your-android-dependency-management-to-a-smoother-setup-0f506452b26f"
  },
  {
    id: 3,
    title: "Unlocking the Joy of Learning Kotlin: A Fun Journey with IntelliJ IDEA",
    excerpt: "Kotlin Beyond Android: Embracing the Multiplatform Revolution with IntelliJ IDEA and JetBrains Academy.",
    date: "Apr 26, 2024",
    readTime: "6 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*b3wX5xfc4rEm8vjGrSIKlA.png",
    link: "https://medium.com/@pallawpathak/unlocking-the-joy-of-learning-kotlin-a-fun-journey-with-intellij-idea-and-jetbrains-academy-1be138692db6"
  },
  {
    id: 4,
    title: "Why You Should Never Use LiveData in UseCase or Repository Classes",
    excerpt: "Understanding the proper boundaries of LiveData and why it should stay in the presentation layer.",
    date: "Jul 21, 2023",
    readTime: "5 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*LVGqlzns4Wo57quwi3cHCA.png",
    link: "https://medium.com/@pallawpathak/why-you-should-never-use-livedata-in-usecase-or-repository-classes-8814d6d5d542"
  },
  {
    id: 5,
    title: "Android CustomView: The Amazing Life Cycle of a View Class",
    excerpt: "Dive deep into the lifecycle of Android CustomViews and understand how to build performant custom UI components.",
    date: "May 24, 2023",
    readTime: "7 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*_D6lZPWxR93buFev2_r2sg.png",
    link: "https://medium.com/@pallawpathak/android-customview-the-amazing-life-cycle-of-a-view-class-3946386bf6ea"
  },
  {
    id: 6,
    title: "Access Multiple Github Accounts Through SSH Key Pair",
    excerpt: "Learn how to manage multiple GitHub accounts seamlessly using SSH key pairs for efficient development workflow.",
    date: "Apr 12, 2023",
    readTime: "4 min read",
    image: "https://miro.medium.com/v2/resize:fill:224:150/1*BpOLBGVYE_AI2XwQxXUj0w.png",
    link: "https://medium.com/@pallawpathak/access-multiple-github-accounts-through-ssh-key-pair-dd3503040cba"
  }
];
